import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, Users, Eye, ShoppingCart, GraduationCap, Store, TrendingUp, Loader2,
  Bell, Send, CreditCard, RefreshCw, BarChart3, Radio,
} from "lucide-react";
import { api, apiError, fileUrl } from "@/lib/api";
import { formatPrice, formatDateTime, stripMd } from "@/lib/format";
import { toast } from "sonner";
import { useAdminAnalytics, useAdminActiveVisitors, useAiNotifications } from "@/hooks/useAi";

const NOTIF_FILTERS = [
  { key: "", label: "Tout" },
  { key: "clients", label: "Clients" },
  { key: "revendeurs", label: "Revendeurs" },
  { key: "ventes", label: "Ventes" },
  { key: "paiements", label: "Paiements" },
  { key: "formations", label: "Formations" },
  { key: "ia", label: "IA" },
];

function Stat({ icon: Icon, label, value, sub, tone = "violet" }) {
  const tones = {
    violet: "from-violet-600 to-indigo-600",
    blue: "from-brand to-brand-dark",
    emerald: "from-emerald-500 to-teal-600",
    amber: "from-amber-500 to-orange-600",
  };
  return (
    <div className="rounded-2xl border border-line bg-white p-4">
      <div className={`mb-2.5 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br ${tones[tone]} text-white`}>
        <Icon className="h-4.5 w-4.5" />
      </div>
      <p className="font-display text-2xl font-black text-ink">{value}</p>
      <p className="text-xs font-semibold text-slate-500">{label}</p>
      {sub && <p className="mt-0.5 text-[11px] text-slate-400">{sub}</p>}
    </div>
  );
}

function RankList({ title, items, render, empty = "Aucune donnée pour le moment." }) {
  return (
    <div className="rounded-2xl border border-line bg-white p-4">
      <h3 className="mb-3 font-display text-sm font-extrabold text-ink">{title}</h3>
      {items?.length ? (
        <ol className="space-y-2.5">
          {items.map((it, i) => (
            <li key={i} className="flex items-center gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-violet-100 text-[11px] font-black text-violet-700">{i + 1}</span>
              {render(it)}
            </li>
          ))}
        </ol>
      ) : (
        <p className="text-xs text-slate-400">{empty}</p>
      )}
    </div>
  );
}

export default function AdminAiAnalytics() {
  const { data: a, isLoading } = useAdminAnalytics();
  const { data: live } = useAdminActiveVisitors();
  const { data: notifs, refetch: refetchNotifs } = useAiNotifications();
  const [report, setReport] = useState("");
  const [generating, setGenerating] = useState(false);
  const [filter, setFilter] = useState("");
  const [chat, setChat] = useState([]);
  const [msg, setMsg] = useState("");
  const [chatting, setChatting] = useState(false);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/ai/admin/report");
      setReport(data.report);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setGenerating(false);
    }
  };

  const sendChat = async () => {
    const text = msg.trim();
    if (!text || chatting) return;
    setMsg("");
    setChat((p) => [...p, { role: "user", content: text }]);
    setChatting(true);
    try {
      const { data } = await api.post("/ai/admin/chat", { message: text, session_id: "admin-chat" });
      setChat((p) => [...p, { role: "assistant", content: data.reply }]);
    } catch (e) {
      setChat((p) => [...p, { role: "assistant", content: apiError(e) }]);
    } finally {
      setChatting(false);
    }
  };

  const markRead = async (id) => {
    await api.post(`/ai/notifications/${id}/read`).catch(() => {});
    refetchNotifs();
  };

  if (isLoading) {
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-violet-600" /></div>;
  }

  const v = a?.visitors || {}, p = a?.products || {}, f = a?.formations || {}, ven = a?.vendors || {}, s = a?.sales || {};
  const filteredNotifs = (notifs?.items || []).filter((n) => !filter || n.category === filter);

  return (
    <div className="space-y-6" data-testid="admin-ai-analytics">
      {/* En-tête */}
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-3xl bg-gradient-to-br from-violet-600 to-indigo-700 p-6 text-white">
        <div className="flex items-center gap-3">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15"><Sparkles className="h-6 w-6" /></span>
          <div>
            <h1 className="font-display text-xl font-black sm:text-2xl">IA & Analytics</h1>
            <p className="text-sm text-white/80">ISMO-TECH AI — vue d'ensemble de toute la plateforme</p>
          </div>
        </div>
        <button
          data-testid="admin-ai-generate-report"
          onClick={generate}
          disabled={generating}
          className="flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-bold text-violet-700 shadow-lg transition-transform hover:scale-105 disabled:opacity-60"
        >
          {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          Générer une analyse
        </button>
      </div>

      {report && (
        <div data-testid="admin-ai-report" className="rounded-2xl border border-violet-200 bg-violet-50/60 p-5">
          <h3 className="mb-2 flex items-center gap-2 font-display text-sm font-extrabold text-violet-800"><Sparkles className="h-4 w-4" /> Analyse du jour</h3>
          <p className="whitespace-pre-wrap text-[13.5px] leading-relaxed text-ink">{stripMd(report)}</p>
        </div>
      )}

      {/* Visiteurs */}
      <div>
        <p className="mb-2.5 text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">Visiteurs</p>
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Stat icon={Users} label="Visiteurs aujourd'hui" value={v.today || 0} tone="violet" />
          <Stat icon={Radio} label="Actifs en ce moment" value={v.active || 0} tone="emerald" />
          <Stat icon={Eye} label="Visiteurs uniques" value={v.unique || 0} tone="blue" />
          <Stat icon={ShoppingCart} label="Commandes du jour" value={s.orders_today || 0} sub={`CA payé : ${formatPrice(s.revenue_total || 0)}`} tone="amber" />
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Visiteurs actifs live */}
        <div className="rounded-2xl border border-line bg-white p-4">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="flex items-center gap-2 font-display text-sm font-extrabold text-ink">
              <span className="flex h-2 w-2"><span className="absolute h-2 w-2 animate-ping rounded-full bg-emerald-400" /><span className="h-2 w-2 rounded-full bg-emerald-500" /></span>
              Visiteurs actuellement actifs
            </h3>
            <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-black text-emerald-700">{live?.count || 0} actifs</span>
          </div>
          <div className="max-h-60 space-y-2 overflow-y-auto">
            {(live?.visitors || []).length ? (
              live.visitors.map((vi, i) => (
                <div key={i} className="flex items-center justify-between rounded-xl bg-surface px-3 py-2 text-xs">
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-ink">{vi.product || vi.page || "—"}</p>
                    <p className="text-[11px] text-slate-400">Session {vi.session} · {vi.status}</p>
                  </div>
                  <span className="shrink-0 text-[11px] font-bold text-slate-500">{Math.max(1, Math.round(vi.duration / 60))} min</span>
                </div>
              ))
            ) : (
              <p className="text-xs text-slate-400">Aucun visiteur actif en ce moment.</p>
            )}
          </div>
        </div>

        {/* Pages populaires */}
        <RankList
          title="Pages populaires"
          items={v.pages}
          render={(it) => (
            <div className="flex w-full items-center justify-between">
              <span className="truncate text-[13px] font-semibold text-ink">{it.page || "/"}</span>
              <span className="text-xs font-bold text-slate-500">{it.views} vues</span>
            </div>
          )}
        />
      </div>

      {/* Produits */}
      <div>
        <p className="mb-2.5 text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">Produits</p>
        <div className="grid gap-4 lg:grid-cols-3">
          <RankList title="Les plus consultés" items={p.top_viewed} render={(it) => (
            <div className="flex w-full items-center gap-2">
              {it.image_url && <img src={fileUrl(it.image_url)} alt="" className="h-8 w-8 rounded-lg object-cover" />}
              <span className="flex-1 truncate text-[13px] font-semibold text-ink">{it.name}</span>
              <span className="text-xs font-bold text-slate-500">{it.n} vues</span>
            </div>
          )} />
          <RankList title="Les plus vendus" items={p.top_sold} render={(it) => (
            <div className="flex w-full items-center gap-2">
              {it.image_url && <img src={fileUrl(it.image_url)} alt="" className="h-8 w-8 rounded-lg object-cover" />}
              <span className="flex-1 truncate text-[13px] font-semibold text-ink">{it.name}</span>
              <span className="text-xs font-bold text-emerald-600">{it.n} ventes</span>
            </div>
          )} />
          <RankList title="Ajoutés au panier" items={p.cart} render={(it) => (
            <div className="flex w-full items-center justify-between">
              <span className="flex-1 truncate text-[13px] font-semibold text-ink">{it.name}</span>
              <span className="text-xs font-bold text-amber-600">{it.n}×</span>
            </div>
          )} />
        </div>
      </div>

      {/* Formations */}
      <div>
        <p className="mb-2.5 text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">Formations</p>
        <div className="grid gap-4 lg:grid-cols-4">
          <div className="lg:col-span-1 grid grid-cols-2 gap-3 lg:grid-cols-1">
            <Stat icon={GraduationCap} label="Progression moyenne" value={`${f.avg_progress || 0}%`} tone="violet" />
            <Stat icon={TrendingUp} label="Formations terminées" value={f.completed || 0} tone="emerald" />
          </div>
          <div className="lg:col-span-3 grid gap-4 sm:grid-cols-2">
            <RankList title="Les plus consultées" items={f.top_viewed} render={(it) => (
              <div className="flex w-full items-center justify-between">
                <span className="flex-1 truncate text-[13px] font-semibold text-ink">{it.title}</span>
                <span className="text-xs font-bold text-slate-500">{it.n} vues</span>
              </div>
            )} />
            <RankList title="Les plus achetées" items={f.top_bought} render={(it) => (
              <div className="flex w-full items-center justify-between">
                <span className="flex-1 truncate text-[13px] font-semibold text-ink">{it.title}</span>
                <span className="text-xs font-bold text-emerald-600">{it.n} élèves</span>
              </div>
            )} />
          </div>
        </div>
      </div>

      {/* Revendeurs */}
      <div>
        <p className="mb-2.5 text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">Revendeurs</p>
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="grid grid-cols-2 gap-3">
            <Stat icon={Store} label="Revendeurs" value={ven.total || 0} tone="blue" />
            <Stat icon={RefreshCw} label="Nouveaux (7j)" value={ven.new || 0} tone="violet" />
          </div>
          <RankList title="Les plus de ventes" items={ven.top} render={(it) => (
            <div className="flex w-full items-center justify-between">
              <span className="flex-1 truncate text-[13px] font-semibold text-ink">{it.shop_name}</span>
              <span className="text-xs font-bold text-emerald-600">{formatPrice(it.revenue)}</span>
            </div>
          )} empty="Aucune vente revendeur." />
          <RankList title="Abonnements à expirer (7j)" items={ven.expiring} render={(it) => (
            <div className="flex w-full items-center justify-between">
              <span className="flex-1 truncate text-[13px] font-semibold text-ink">{it.shop_name}</span>
              <span className="text-xs font-bold text-amber-600">{formatDateTime(it.expires_at)}</span>
            </div>
          )} empty="Aucun abonnement proche de l'expiration." />
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Centre de notifications */}
        <div className="rounded-2xl border border-line bg-white p-4" data-testid="admin-notification-center">
          <h3 className="mb-3 flex items-center gap-2 font-display text-sm font-extrabold text-ink"><Bell className="h-4 w-4 text-violet-600" /> Centre de notifications</h3>
          <div className="mb-3 flex flex-wrap gap-1.5">
            {NOTIF_FILTERS.map((nf) => (
              <button key={nf.key} data-testid={`admin-notif-filter-${nf.key || "all"}`} onClick={() => setFilter(nf.key)}
                className={`rounded-full px-2.5 py-1 text-[11px] font-bold transition-colors ${filter === nf.key ? "bg-violet-600 text-white" : "border border-line text-slate-500 hover:border-violet-300"}`}>
                {nf.label}
              </button>
            ))}
          </div>
          <div className="max-h-72 space-y-2 overflow-y-auto">
            {filteredNotifs.length ? filteredNotifs.map((n) => (
              <Link key={n.id} to={n.link || "#"} onClick={() => markRead(n.id)}
                className={`block rounded-xl border p-3 transition-colors hover:border-violet-300 ${n.read ? "border-line bg-white" : "border-violet-200 bg-violet-50/60"}`}>
                <div className="flex items-center justify-between">
                  <p className="text-[13px] font-bold text-ink">{n.title}</p>
                  {!n.read && <span className="h-2 w-2 rounded-full bg-violet-500" />}
                </div>
                <p className="mt-0.5 whitespace-pre-wrap text-[11.5px] text-slate-500">{n.message}</p>
                <p className="mt-1 text-[10px] text-slate-400">{formatDateTime(n.created_at)}</p>
              </Link>
            )) : <p className="text-xs text-slate-400">Aucune notification.</p>}
          </div>
        </div>

        {/* Assistant analytique */}
        <div className="flex flex-col rounded-2xl border border-line bg-white p-4">
          <h3 className="mb-3 flex items-center gap-2 font-display text-sm font-extrabold text-ink"><BarChart3 className="h-4 w-4 text-violet-600" /> Demander à ISMO-TECH AI</h3>
          <div className="mb-3 max-h-56 flex-1 space-y-2 overflow-y-auto">
            {chat.length === 0 && <p className="text-xs text-slate-400">Ex : « Quel est le produit le plus vendu ? », « Combien de paiements à vérifier ? »</p>}
            {chat.map((m, i) => (
              <div key={i} className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
                <div className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3 py-2 text-[12.5px] ${m.role === "user" ? "bg-violet-600 text-white" : "border border-line bg-surface text-ink"}`}>{stripMd(m.content)}</div>
              </div>
            ))}
            {chatting && <div className="flex items-center gap-2 text-xs text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Analyse…</div>}
          </div>
          <div className="flex items-center gap-2">
            <input data-testid="admin-ai-chat-input" value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendChat()}
              placeholder="Posez une question analytique…" className="flex-1 rounded-full border border-line bg-surface px-3.5 py-2 text-[13px] outline-none focus:border-violet-500" />
            <button data-testid="admin-ai-chat-send" onClick={sendChat} disabled={chatting} className="flex h-9 w-9 items-center justify-center rounded-full bg-violet-600 text-white disabled:opacity-50">
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
